import {
  Property,
  CreateProperty,
  UpdateProperty,
  AvailabilityPeriod,
} from '@/integrations/mysql/types';
import { getApiEndpoint } from '@/config/api';
import { authService } from '@/services/authService';
import { apiJson, apiJsonArray } from '@/lib/api-client';

export class PropertyService {
  static async getPropertyById(id: number): Promise<Property | null> {
    const p = await apiJson<Property | null>(`api/properties/${id}`);
    return p;
  }

  static async getPropertyByIdSimple(id: number): Promise<Property | null> {
    return apiJson<Property | null>(`api/properties/${id}?variant=simple`);
  }

  static async getPropertyByIdAnyStatus(id: number): Promise<Property | null> {
    return apiJson<Property | null>(`api/properties/${id}?variant=any`);
  }

  static async getAllProperties(
    limit: number = 50,
    offset: number = 0,
  ): Promise<Property[]> {
    return apiJsonArray<Property>(
      `api/properties/list/available?limit=${limit}&offset=${offset}`,
    );
  }

  static async getAllPropertiesForAdmin(
    limit: number = 10000,
    offset: number = 0,
  ): Promise<Property[]> {
    return apiJsonArray<Property>(
      `api/properties/admin/list?limit=${limit}&offset=${offset}`,
    );
  }

  static async getTotalPropertiesCount(
    includeInactive: boolean = true,
  ): Promise<number> {
    const r = await apiJson<{ total: number }>(
      `api/properties/count?includeInactive=${includeInactive}`,
    );
    return r.total;
  }

  static async getAllPropertiesForSearch(): Promise<Property[]> {
    return apiJsonArray<Property>('api/properties/list/search');
  }

  static async getPropertiesByOwner(ownerId: number): Promise<Property[]> {
    return apiJsonArray<Property>(`api/properties/by-owner/${ownerId}`);
  }

  static async getPropertiesByCity(city: string): Promise<Property[]> {
    return apiJsonArray<Property>(
      `api/properties/by-city/${encodeURIComponent(city)}`,
    );
  }

  static async getPropertiesByType(propertyType: string): Promise<Property[]> {
    return apiJsonArray<Property>(
      `api/properties/by-type/${encodeURIComponent(propertyType)}`,
    );
  }

  static async getPropertiesByPriceRange(
    minPrice: number,
    maxPrice: number,
  ): Promise<Property[]> {
    return apiJsonArray<Property>(
      `api/properties/by-price?min=${minPrice}&max=${maxPrice}`,
    );
  }

  static async getPropertiesByGuests(maxGuests: number): Promise<Property[]> {
    return apiJsonArray<Property>(`api/properties/by-guests/${maxGuests}`);
  }

  static async searchProperties(filters: {
    city?: string;
    propertyType?: string;
    minPrice?: number;
    maxPrice?: number;
    maxGuests?: number;
    bedrooms?: number;
    limit?: number;
    offset?: number;
  }): Promise<Property[]> {
    return apiJsonArray<Property>('api/properties/filter', {
      method: 'POST',
      body: JSON.stringify(filters),
    });
  }

  static async createProperty(propertyData: CreateProperty): Promise<Property> {
    return apiJson<Property>('api/properties', {
      method: 'POST',
      body: JSON.stringify(propertyData),
    });
  }

  static async updateProperty(
    id: number,
    propertyData: UpdateProperty,
  ): Promise<Property | null> {
    const authData = authService.getAuthData();
    const token = authData.token;
    if (!token) {
      throw new Error(
        'Token de autentica��o n�o encontrado. Por favor, fa�a login novamente.',
      );
    }
    const response = await fetch(getApiEndpoint(`/api/properties/${id}`), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(propertyData),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        error: 'Erro desconhecido',
      }));
      throw new Error(
        (errorData as { error?: string }).error ||
          `HTTP error! status: ${response.status}`,
      );
    }
    return response.json();
  }

  static async deleteProperty(id: number): Promise<boolean> {
    const r = await apiJson<{ success: boolean }>(`api/properties/${id}`, {
      method: 'DELETE',
    });
    return r.success;
  }

  static async setShareForSale(
    propertyId: number,
    forSale: boolean,
  ): Promise<{ id: number; is_share_for_sale: boolean }> {
    const authData = authService.getAuthData();
    const token = authData.token;
    if (!token) {
      throw new Error(
        'Token de autentica??o n?o encontrado. Por favor, fa?a login novamente.',
      );
    }
    const response = await fetch(
      getApiEndpoint(`api/properties/${propertyId}/share-for-sale`),
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ for_sale: forSale }),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        error: 'Erro desconhecido',
      }));
      throw new Error(
        (errorData as { error?: string }).error ||
          `HTTP error! status: ${response.status}`,
      );
    }
    const data = (await response.json()) as {
      id: number;
      is_share_for_sale: number | boolean;
    };
    return {
      id: data.id,
      is_share_for_sale: Boolean(data.is_share_for_sale),
    };
  }

  static async getPropertiesWithAvailability(): Promise<
    (Property & { availability_periods: AvailabilityPeriod[] })[]
  > {
    return apiJsonArray<Property & { availability_periods: AvailabilityPeriod[] }>(
      'api/properties/with-availability',
    );
  }

  static async getAvailablePropertiesInPeriod(
    startDate: Date,
    endDate: Date,
  ): Promise<Property[]> {
    const s = startDate.toISOString().slice(0, 10);
    const e = endDate.toISOString().slice(0, 10);
    return apiJsonArray<Property>(
      `api/properties/available-in-period?start=${s}&end=${e}`,
    );
  }
}

export const propertyService = new PropertyService();
